import React, { useEffect, useRef, useState, useCallback } from "react";
import clsx from "clsx";

function useMousePosition() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return mousePosition;
}

const hexToRgb = (hex) => {
  hex = hex.replace("#", "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const hexInt = parseInt(hex, 16);
  const red = (hexInt >> 16) & 255;
  const green = (hexInt >> 8) & 255;
  const blue = hexInt & 255;
  return [red, green, blue];
};

const Particles = ({
  className = "",
  quantity = 100,
  staticity = 50,
  ease = 100,
  size = 1,
  refresh = false,
  color = "#ffffff",
  vx = 0.01,
  vy = 0.01,
  maxSpeed = 0.1,
  enable = true, // New parameter to enable/disable new particles spawning
}) => {
  const canvasRef = useRef(null);
  const context = useRef(null);
  const circles = useRef([]);
  const mousePosition = useMousePosition();
  const mouse = useRef({ x: 0, y: 0 });
  const dpr = window.devicePixelRatio || 1;

  // Function to generate parameters for circles
  const circleParams = useCallback(() => {
    const x = Math.random() * window.innerWidth; // Random x position within viewport width
    const y = Math.random() * window.innerHeight; // Random y position within viewport height
    const pSize = Math.random() * 2 + size;
    const alpha = 0;
    const targetAlpha = parseFloat((Math.random() * 0.6 + 0.4).toFixed(1));

    // Generate random velocities between -maxSpeed and maxSpeed
    const dx = (Math.random() * 2 - 1) * maxSpeed;
    const dy = (Math.random() * 2 - 1) * maxSpeed;

    const magnetism = 0.1 + Math.random() * 2;
    return { x, y, translateX: 0, translateY: 0, size: pSize, alpha, targetAlpha, dx, dy, magnetism };
  }, [size, maxSpeed]);

  const resizeCanvas = useCallback(() => {
    if (canvasRef.current && context.current) {
      circles.current.length = 0;
      const width = window.innerWidth;
      const height = window.innerHeight;

      canvasRef.current.width = width * dpr;
      canvasRef.current.height = height * dpr;
      canvasRef.current.style.width = `${width}px`;
      canvasRef.current.style.height = `${height}px`;

      context.current.scale(dpr, dpr);
    }
  }, [dpr]);

  const drawCircle = useCallback(
    (circle, update = false) => {
      if (context.current) {
        const { x, y, translateX, translateY, size, alpha } = circle;
        context.current.translate(translateX, translateY);
        context.current.beginPath();
        context.current.arc(x, y, size, 0, 2 * Math.PI);
        context.current.fillStyle = `rgba(${hexToRgb(color).join(", ")}, ${alpha || 0.5})`;
        context.current.fill();
        context.current.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (!update) {
          circles.current.push(circle);
        }
      }
    },
    [color, dpr]
  );

  const clearContext = useCallback(() => {
    if (context.current) {
      context.current.clearRect(0, 0, window.innerWidth, window.innerHeight);
    }
  }, []);

  const drawParticles = useCallback(() => {
    clearContext();
    for (let i = 0; i < quantity; i++) {
      const circle = circleParams();
      drawCircle(circle);
    }
  }, [quantity, clearContext, drawCircle, circleParams]);

  const animate = useCallback(() => {
    clearContext();
    circles.current.forEach((circle, i) => {
      circle.x += circle.dx + vx;
      circle.y += circle.dy + vy;
      circle.translateX += (mouse.current.x / (staticity / circle.magnetism) - circle.translateX) / ease;
      circle.translateY += (mouse.current.y / (staticity / circle.magnetism) - circle.translateY) / ease;
      drawCircle(circle, true);

      // Remove the circle from the array if it goes out of bounds
      if (
        circle.x < -circle.size ||
        circle.x > window.innerWidth + circle.size ||
        circle.y < -circle.size ||
        circle.y > window.innerHeight + circle.size
      ) {
        circles.current.splice(i, 1);
        // Only add a new circle if `enable` is true
        if (enable) {
          drawCircle(circleParams());
        }
      }
    });
    window.requestAnimationFrame(animate);
  }, [clearContext, drawCircle, ease, staticity, vx, vy, circleParams, enable]);

  const onMouseMove = useCallback(() => {
    const { innerWidth, innerHeight } = window;
    const x = mousePosition.x - innerWidth / 2;
    const y = mousePosition.y - innerHeight / 2;
    const inside = x < innerWidth / 2 && x > -innerWidth / 2 && y < innerHeight / 2 && y > -innerHeight / 2;
    if (inside) {
      mouse.current.x = x;
      mouse.current.y = y;
    }
  }, [mousePosition]);

  const initCanvas = useCallback(() => {
    resizeCanvas();
    drawParticles();
  }, [resizeCanvas, drawParticles]);

  // useEffect to initialize the canvas
  useEffect(() => {
    if (canvasRef.current) {
      context.current = canvasRef.current.getContext("2d");
    }
    initCanvas();
    animate();
    window.addEventListener("resize", initCanvas);

    return () => {
      window.removeEventListener("resize", initCanvas);
    };
  }, [initCanvas, animate]);

  // useEffect to track mouse movement
  useEffect(() => {
    onMouseMove();
  }, [onMouseMove, mousePosition]);

  return (
    <canvas
      ref={canvasRef}
      className={clsx("pointer-events-none absolute top-0 left-0 w-full h-full", className)}
      style={{
        position: "fixed",
        zIndex: -1, // Ensure the canvas stays behind the content
        pointerEvents: "none",
      }}
    />
  );
};

export default Particles;
